
import React, { useEffect, useCallback } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Star, ExternalLink } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';
import useEmblaCarousel from 'embla-carousel-react';

interface ClientLogo {
  id: number;
  name: string;
  logoUrl: string;
  websiteUrl: string;
}

interface ClientTestimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  avatarUrl: string;
  testimonial: string;
  rating: number;
}

const clientLogos: ClientLogo[] = [
  { 
    id: 1, 
    name: "Humboldt Labor", 
    logoUrl: "/images/clients/Logo_Humboldt Labor.png", 
    websiteUrl: "https://www.humboldt-labor.de/"
  },
  { 
    id: 2, 
    name: "BWI", 
    logoUrl: "/images/clients/bwi.png", 
    websiteUrl: "http://www.bwi.de"
  },
  { 
    id: 3, 
    name: "deals.com", 
    logoUrl: "/images/clients/deals.png", 
    websiteUrl: "http://deals.com"
  },
  { 
    id: 4, 
    name: "eye-square", 
    logoUrl: "/images/clients/eyesquare.png", 
    websiteUrl: "http://www.eye-square.com"
  },
  { 
    id: 5, 
    name: "FAU GmbH", 
    logoUrl: "/images/clients/fau.png", 
    websiteUrl: "https://www.fau-gmbh.de"
  },
  { 
    id: 6, 
    name: "From Light 2 Art", 
    logoUrl: "/images/clients/fromlight2art.png", 
    websiteUrl: "https://fromlight2art.com/"
  },
  { 
    id: 7, 
    name: "Hot Rats Media", 
    logoUrl: "/images/clients/hotratsmedia.png", 
    websiteUrl: "https://www.hotratsmedia.com/"
  },
  { 
    id: 8, 
    name: "IDG", 
    logoUrl: "/images/clients/idg.png", 
    websiteUrl: "https://www.idg.de/"
  },
  { 
    id: 9, 
    name: "Euronorm", 
    logoUrl: "/images/clients/euronorm.svg", 
    websiteUrl: "https://www.euronorm.de/"
  },
  { 
    id: 10, 
    name: "Christian Hüller", 
    logoUrl: "/images/clients/hueller.png", 
    websiteUrl: "https://christianhueller.de/"
  },
  { 
    id: 11, 
    name: "Hartensteiner", 
    logoUrl: "/images/clients/hartensteiner.png", 
    websiteUrl: "https://www.hartensteiner.com/"
  },
  { 
    id: 12, 
    name: "UNMF", 
    logoUrl: "/images/clients/unmf.png", 
    websiteUrl: "https://unmf.berlin/"
  },
  { 
    id: 13, 
    name: "BEYHS", 
    logoUrl: "/images/clients/beys.png", 
    websiteUrl: "https://wp-beyhs.de/"
  },
  { 
    id: 14, 
    name: "Yusuf Sahilli", 
    logoUrl: "/images/clients/yusuf.png", 
    websiteUrl: "https://www.yusufsahilli.de/"
  },
  { 
    id: 15, 
    name: "Optimum", 
    logoUrl: "/images/clients/optimum.png", 
    websiteUrl: "http://www.optimum-steuern.de"
  },
  { 
    id: 16, 
    name: "PeterUlrich", 
    logoUrl: "/images/clients/ulrich.png", 
    websiteUrl: "https://peterulrich.net/"
  },
  { 
    id: 17, 
    name: "Salefox", 
    logoUrl: "/images/clients/salefox.png", 
    websiteUrl: "https://salefox-online.de/"
  },
  { 
    id: 18, 
    name: "SAENA", 
    logoUrl: "/images/clients/saena.png", 
    websiteUrl: "https://www.saena.de/"
  },
  { 
    id: 19, 
    name: "Velocity 9", 
    logoUrl: "/images/clients/velocity9.png", 
    websiteUrl: "https://www.velocity-9.com/"
  }
];

const clientTestimonials: ClientTestimonial[] = [
  {
    id: 1,
    name: "Christian Hüller",
    role: "Fotograf",
    company: "Hüller Unternehmensfotografie",
    avatarUrl: "https://i.pravatar.cc/150?img=1",
    testimonial: "Es ist mir immer eine große Freude, mit Matthias zusammenzuarbeiten. Videoanfragen reiche ich immer gerne an ihn weiter. Hier wird gewerkelt, bis der Kunde vollkommen zufrieden ist.",
    rating: 5
  },
  {
    id: 2,
    name: "Chris de Burgh",
    role: "Musician",
    company: "VeloCity 9",
    avatarUrl: "https://i.pravatar.cc/150?img=3",
    testimonial: "Starker Typ - starke Videos. Eine Bereicherung für jeden Backstage Raum!",
    rating: 5
  },
  {
    id: 3,
    name: "Mark Rother",
    role: "Ehemann",
    company: "Hochzeit",
    avatarUrl: "https://i.pravatar.cc/150?img=5",
    testimonial: "Ich finds Hammer. Die Gattin ist peinlich berührt, aber alles wo sie nicht drauf zu sehen ist, gefällt ihr gut. Danke Meyfi!",
    rating: 5
  },
  {
    id: 4,
    name: "Dr. Jürgen Kiefer",
    role: "Product Manager",
    company: "EcoSolutions",
    avatarUrl: "https://i.pravatar.cc/150?img=8",
    testimonial: "Matthias provides design solutions that are clear, comprehensive, and consistent. I truly value his precision and level of detail. Further, Matthias is a researcher who understands humans showing the ability to apply his knowledge in a business-relevant direction. Lastly, Matthias is a true problem solver with an understanding of technological solutions that helped me various times in our collaboration on common UX design and research projects. Above all, I value his reliability, his persistance, and his friendliness. ",
    rating: 5
  }
];

export const ClientsSection = () => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();

  const itemsPerView = isMobile ? 1 : 4;
  
  // Initialize the embla carousel with options
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false
  });
  
  // Auto-rotation function with proper cleanup
  const autoplay = useCallback(() => {
    if (!emblaApi) return;
    
    const autoplayTimer = setTimeout(() => {
      if (!emblaApi.canScrollNext()) {
        emblaApi.scrollTo(0);
      } else {
        emblaApi.scrollNext();
      }
      autoplay();
    }, 4000);
    
    return () => clearTimeout(autoplayTimer);
  }, [emblaApi]);
  
  // Set up auto-rotation effect with proper cleanup
  useEffect(() => {
    if (!emblaApi) return;
    
    // Start autoplay
    const cleanup = autoplay();
    
    return () => {
      // Clean up autoplay timer
      if (cleanup) cleanup();
      
      // Clean up event listeners - fixed by passing the correct parameters
      emblaApi.off('select', () => {});
    };
  }, [emblaApi, autoplay]);
  
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl font-bold mb-8 text-center">{t('Clients')}</h2>
        <p className="text-xl mb-12 text-muted-foreground max-w-3xl mx-auto text-center">
          {t('Trusted by innovative companies around the world')}
        </p>
        
        {/* Client logos carousel */}
        <div className="mb-24">
          <div className="embla overflow-hidden" ref={emblaRef}>
            <div className="embla__container flex">
              {clientLogos.map((client) => (
                <div 
                  key={client.id} 
                  className={`embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_${100 / itemsPerView}%] p-4`}
                >
                  <a 
                    href={client.websiteUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="h-24 bg-gray-50 rounded-md flex items-center justify-center p-4 border border-gray-100 hover:border-primary/20 transition-all duration-300 group relative"
                  >
                    <img 
                      src={client.logoUrl} 
                      alt={client.name} 
                      className="max-h-full max-w-full object-contain transition-all duration-300"
                    />
                    <span className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <ExternalLink className="h-5 w-5 text-gray-600 opacity-0 group-hover:opacity-100" />
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Testimonials carousel */}
        <Carousel
          opts={{
            align: "start",
          }}
          className="w-full"
        >
          <CarouselContent>
            {clientTestimonials.map((testimonial) => (
              <CarouselItem key={testimonial.id} className="md:basis-1/2 lg:basis-1/2">
                <Card className="m-4 border-none shadow-lg bg-gradient-to-br from-white to-gray-50">
                  <CardContent className="p-6">
                    <div className="flex flex-col space-y-4">
                      <div className="flex space-x-1">
                        {Array(5).fill(0).map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-5 w-5 ${
                              i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                            }`} 
                          />
                        ))}
                      </div>
                      
                      <p className="italic text-muted-foreground">"{testimonial.testimonial}"</p>
                      
                      <div className="flex items-center space-x-4 pt-4">
                        <Avatar>
                          <img src={testimonial.avatarUrl} alt={testimonial.name} />
                        </Avatar>
                        <div>
                          <p className="font-medium">{testimonial.name}</p>
                          <p className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-2 mt-4">
            <CarouselPrevious />
            <CarouselNext />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
